// File: src/pages/admin/OrderManagement.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  TextField,
  Avatar,
  Tabs,
  Tab,
  Pagination,
  InputAdornment,
  Stack,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Delete,
  Edit,
  Refresh,
  Visibility,
  Person,
  AttachMoney,
  Comment,
  DeleteSweep,
  ArrowBack,
  Search,
  QrCode2,
  Download,
  Print,
  Close,
  Image as ImageIcon,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useDeleteCommentMutation,
  useGenerateQRMutation,
  useLazyGetQrImageQuery,
} from '../../features/admin/OrderlistApi.ts';
import { Order, OrderComment, ORDER_STATUS_OPTIONS } from '../../features/admin/OrderlistApi.ts';
import { determineOrderType } from '../../features/admin/OrderlistApi.ts';

// Warehouses dropdown
import { useListWarehousesQuery } from '../../features/admin/warehouseApi.ts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-details-tabpanel-${index}`}
      aria-labelledby={`order-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const OrderManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const qrPrintRef = useRef<HTMLDivElement>(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [mainTabValue, setMainTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [viewCommentsOpen, setViewCommentsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletableComments, setDeletableComments] = useState<OrderComment[]>([]);
  const [deleteAllCommentsDialogOpen, setDeleteAllCommentsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [commentText, setCommentText] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [generatedQR, setGeneratedQR] = useState<{ pickupCode: string; qrCode: string } | null>(
    null
  );
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Track the last update time for auto-refresh (hidden from UI)
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  // ---- Orders query with silent polling for auto-refresh ----
  const {
    data,
    isLoading,
    error,
    isError,
    refetch: refetchOrders,
  } = useListOrdersQuery(
    {
      ...(warehouseFilter !== '' && warehouseFilter !== undefined ? { warehouse_id: warehouseFilter } : {}),
      _t: lastUpdateTime, // Cache busting timestamp
    },
    {
      // Enable silent polling for auto-refresh every 10 seconds
      pollingInterval: 10000, // 10 seconds
      // Refetch when window regains focus
      refetchOnFocus: true,
      // Refetch when reconnecting
      refetchOnReconnect: true,
    }
  );

  // Warehouses for dropdown
  const { data: warehousesData } = useListWarehousesQuery();
  const warehouses = warehousesData?.warehouses || [];

  // Update order status mutation with automatic cache update
  const [updateOrderStatus, { isLoading: isUpdating }] = useUpdateOrderStatusMutation();
  
  const [deleteOrder, { isLoading: isDeleting }] = useDeleteOrderMutation();
  const [deleteComment, { isLoading: isDeletingComment }] = useDeleteCommentMutation();
  const [generateQR, { isLoading: isGeneratingQR }] = useGenerateQRMutation();
  const [triggerGetQrImage, { isLoading: isLoadingQrImage }] = useLazyGetQrImageQuery();

  const orders = data?.orders || [];
  const totalRecords = orders.length;

  // Silent auto-refresh effect - trigger refresh periodically without UI indicators
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) { // Only refresh if page is visible
        // Silent refresh - no UI indication
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearTimeout(interval);
  }, []);

  // Listen for visibility changes (silent refresh when page becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Silent refresh when page becomes visible
        refetchOrders();
        setLastUpdateTime(Date.now());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Filter orders based on search and status
  const filteredOrders = orders.filter((order: Order) => {
    if (!searchStr && statusFilter.length === 0) return true;

    const search = searchStr.toLowerCase();
    const matchesSearch =
      !searchStr ||
      order.id.toString().includes(search) ||
      (order.user_name && order.user_name.toLowerCase().includes(search)) ||
      order.user_id?.toString().includes(search);

    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(order.status);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const paginatedOrders = filteredOrders.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setStatusFilter(typeof value === 'string' ? value.split(',').map(Number) : value);
    setPage(1);
  };

  const handleWarehouseFilterChange = (event: any) => {
    const value = event.target.value;
    setWarehouseFilter(value === '' ? '' : Number(value));
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchStr('');
    setStatusFilter([]);
    setWarehouseFilter('');
    setPage(1);
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setDetailsTabValue(0);
    setShowDetails(true);
  };

  const handleBackToList = () => {
    setShowDetails(false);
    setSelectedOrder(null);
  };

  const handleStatusUpdateClick = (order: Order) => {
    setSelectedOrder(order);
    setSelectedStatus(order.status);
    setStatusDialogOpen(true);
  };

  const handleDeleteClick = (order: Order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const handleDeleteAllCommentsClick = (order: Order) => {
    setSelectedOrder(order);

    const deletable = (order.comments || []).filter((c) => {
      const s = Number((c as any).status);
      return !Number.isNaN(s) && requiresComment(s);
    });

    setDeletableComments(deletable);
    setDeleteAllCommentsDialogOpen(true);
  };

  const handleConfirmDeleteAllComments = async () => {
    if (!selectedOrder) return;

    if (!deletableComments || deletableComments.length === 0) {
      setSnackbar({
        open: true,
        message: 'No comments eligible for deletion (only statuses 5-10 can be deleted).',
        severity: 'info',
      });
      setDeleteAllCommentsDialogOpen(false);
      return;
    }

    try {
      const promiseList = deletableComments.map((comment) =>
        deleteComment({
          comment_id: comment.id!,
          order_id: selectedOrder.id,
        })
          .unwrap()
          .then(
            (res) => ({ status: 'fulfilled', res, commentId: comment.id }),
            (err) => ({ status: 'rejected', err, commentId: comment.id })
          )
      );

      const results = await Promise.all(promiseList);

      const failed = results.filter((r) => (r as any).status === 'rejected') as any[];
      const succeeded = results.filter((r) => (r as any).status === 'fulfilled') as any[];

      if (failed.length === 0) {
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} comment(s) successfully.`,
          severity: 'success',
        });
      } else if (succeeded.length === 0) {
        const messages = failed.map(
          (f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`
        );
        setSnackbar({
          open: true,
          message: messages.join(' | '),
          severity: 'error',
        });
      } else {
        const failMsgs = failed.map(
          (f) => f.err?.data?.message || f.err?.message || `Failed to delete comment ${f.commentId}`
        );
        setSnackbar({
          open: true,
          message: `Deleted ${succeeded.length} comment(s). Failed: ${failMsgs.join(' | ')}`,
          severity: 'warning',
        });
      }

      setDeleteAllCommentsDialogOpen(false);
      // Silent auto-refresh after deletion
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete all comments:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Error deleting comments',
        severity: 'error',
      });
      setDeleteAllCommentsDialogOpen(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrder) return;

    try {
      const result = await deleteOrder({ order_id: selectedOrder.id }).unwrap();

      setSnackbar({
        open: true,
        message: result?.message || 'Order deleted successfully!',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      
      // If we're in details view, go back to list
      if (showDetails) {
        setShowDetails(false);
        setSelectedOrder(null);
      }
      
      // Silent auto-refresh after deletion
      refetchOrders();
      setLastUpdateTime(Date.now());
    } catch (err: any) {
      console.error('Failed to delete order:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error deleting order',
        severity: 'error',
      });
    }
  };

  const handleStatusUpdate = async (comment?: string) => {
    if (!selectedOrder) return;

    try {
      const updateData: any = {
        order_id: selectedOrder.id,
        status: selectedStatus,
      };

      if (comment && comment.trim()) {
        updateData.comment = comment;
      }

      // Optimistically update the UI immediately
      const updatedOrder = { ...selectedOrder, status: selectedStatus };
      setSelectedOrder(updatedOrder);

      const result = await updateOrderStatus(updateData).unwrap();

      setSnackbar({
        open: true,
        message: result?.message || 'Order status updated successfully!',
        severity: 'success',
      });
      setStatusDialogOpen(false);
      setCommentDialogOpen(false);
      setCommentText('');
      
      // Silent auto-refresh to get updated data from server
      refetchOrders();
      setLastUpdateTime(Date.now());
      
    } catch (err: any) {
      console.error('Failed to update order status:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Error updating order status',
        severity: 'error',
      });
      
      // Silent auto-refresh on error too
      refetchOrders();
      setLastUpdateTime(Date.now());
    }
  };

  const handleCommentSubmit = () => {
    handleStatusUpdate(commentText);
  };

  const handleViewComments = (order: Order) => {
    setSelectedOrder(order);
    setViewCommentsOpen(true);
  };

  // Silent refresh function (no UI indication)
  const handleSilentRefresh = () => {
    refetchOrders();
    setLastUpdateTime(Date.now());
  };

  // ---------- status helpers ----------
  const resolveStatusOption = (status: number | string | undefined) => {
    if (status === undefined || status === null) return null;
    const num = Number(status);
    if (Number.isNaN(num)) return null;
    return ORDER_STATUS_OPTIONS.find((opt) => opt.value === num) || null;
  };

  const inferStatusFromCommentText = (text?: string) => {
    if (!text) return null;
    const normalized = String(text).toLowerCase();
    for (const opt of ORDER_STATUS_OPTIONS) {
      if (!opt || !opt.label) continue;
      const label = String(opt.label).toLowerCase();
      if (normalized.includes(label)) return opt;
    }
    return null;
  };

  const CommentItem = ({ comment }: { comment: OrderComment }) => {
    const statusLabelFromApi = (comment as any).status_label || (comment as any).status_text || null;
    const inferredOption = inferStatusFromCommentText(comment.comment);
    const fromStatusField = resolveStatusOption((comment as any).status);

    let chipLabel: string | undefined;
    let chipColor = '#9e9e9e';

    if (statusLabelFromApi) {
      chipLabel = String(statusLabelFromApi);
      const matched = ORDER_STATUS_OPTIONS.find(
        (o) => String(o.label).toLowerCase() === String(statusLabelFromApi).toLowerCase()
      );
      if (matched) chipColor = matched.color;
    } else if (inferredOption) {
      chipLabel = inferredOption.label;
      chipColor = inferredOption.color;
    } else if (fromStatusField) {
      chipLabel = fromStatusField.label;
      chipColor = fromStatusField.color;
    } else if ((comment as any).status !== undefined && (comment as any).status !== null) {
      chipLabel = `Status ${(comment as any).status}`;
    } else {
      chipLabel = undefined;
    }

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: comment.user_type === 'admin' ? '#f8f9ff' : '#f0f8ff',
          borderRadius: 2,
          border: `1px solid ${comment.user_type === 'admin' ? '#e3f2fd' : '#e8f5e8'}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: comment.user_type === 'admin' ? '#1976d2' : '#4caf50',
                width: 32,
                height: 32,
              }}
            >
              {comment.user_type === 'admin' ? 'A' : 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {comment.user_type === 'admin' ? 'Admin' : `User: ${comment.user_name || comment.user_id}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {comment.created_at ? formatDateTime(comment.created_at) : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {chipLabel && (
              <Chip
                label={chipLabel}
                size="small"
                sx={{
                  backgroundColor: chipColor,
                  color: 'white',
                  height: 20,
                }}
              />
            )}
          </Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            pl: 6,
            fontStyle: 'italic',
            color: 'text.primary',
          }}
        >
          "{comment.comment}"
        </Typography>
      </Box>
    );
  };

  const CommentsList = ({
    orderId,
    maxHeight = 400,
    showDeleteAllButton = false,
  }: {
    orderId: number;
    maxHeight?: number;
    showDeleteAllButton?: boolean;
  }) => {
    const orderComments = getCommentsForOrder(orderId);
    const order = orders.find((o) => o.id === orderId);

    if (orderComments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No comments available for this order.
        </Typography>
      );
    }

    return (
      <Box>
        {showDeleteAllButton && orderComments.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweep />}
              onClick={() => order && handleDeleteAllCommentsClick(order)}
              disabled={isDeletingComment}
            >
              Delete All comments ({orderComments.length})
            </Button>
          </Box>
        )}

        <Box sx={{ maxHeight, overflowY: 'auto' }}>
          {orderComments
            .slice()
            .sort(
              (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
            )
            .map((comment, index) => (
              <CommentItem key={comment.id || index} comment={comment} />
            ))}
        </Box>
      </Box>
    );
  };

  // Agent info helper
  const renderAgentInfo = (order: Order | null | undefined) => {
    const agent = (order && (order as any).delivery_agent) || null;
    if (!agent)
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );

    const name =
      agent.first_name ||
      agent.firstName ||
      agent.user_name ||
      `${agent.first_name || ''} ${agent.last_name || ''}`.trim() ||
      `Agent ${agent.id || ''}`;
    const contact = agent.contact || agent.mobile || agent.phone || null;
    const email = agent.email || null;

    return (
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: '#ff6a00', textTransform: 'uppercase' }}
        >
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {contact ? (
            contact
          ) : email ? (
            <span style={{ fontFamily: 'monospace' }}>{email}</span>
          ) : (
            `ID: ${agent.id || '-'}`
          )}
        </Typography>
        {email && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontFamily: 'monospace' }}
          >
            {email}
          </Typography>
        )}
      </Box>
    );
  };

  // order type for QR
  const getOrderType = (order: any) => {
    try {
      const inferred = determineOrderType(order);
      return inferred === 'guest' ? 'guest' : 'regular';
    } catch (e) {
      return 'regular';
    }
  };

  const handleGenerateQR = async (order: Order) => {
    try {
      const fullOrder = data?.orders?.find((o) => o.id === order.id) || order;

      const orderType = getOrderType(fullOrder);

      setSnackbar({
        open: true,
        message: `Generating QR Code for ${orderType} order...`,
        severity: 'info',
      });

      const result = await generateQR({
        order_id: order.id,
        order_type: orderType as 'regular' | 'guest',
      }).unwrap();

      if (result.success) {
        setGeneratedQR({
          pickupCode: result.pickup_code,
          qrCode: result.qr_code,
        });

        try {
          const imageResult = await triggerGetQrImage({
            order_id: order.id,
            order_type: orderType as 'regular' | 'guest',
          }).unwrap();

          try {
            const maybeBlob = imageResult as unknown as Blob;
            if (
              maybeBlob &&
              (maybeBlob instanceof Blob ||
                ((maybeBlob as any).size !== undefined && (maybeBlob as any).type !== undefined))
            ) {
              const imageUrl = URL.createObjectURL(maybeBlob as Blob);
              setQrImageUrl(imageUrl);
            } else {
              setQrImageUrl(null);
            }
          } catch (imgInnerErr) {
            console.warn('Unable to create object URL from imageResult', imgInnerErr);
            setQrImageUrl(null);
          }
        } catch (imgErr: any) {
          console.warn('QR image fetch failed (optional):', imgErr);
          setQrImageUrl(null);
        }

        setSelectedOrder(order);
        setQrDialogOpen(true);

        setSnackbar({
          open: true,
          message: `QR Code generated successfully for ${orderType} order!`,
          severity: 'success',
        });
      } else {
        const msg = result.message || 'QR generation failed';
        setSnackbar({
          open: true,
          message: msg,
          severity: msg.toLowerCase().includes('not found') ? 'error' : 'warning',
        });
      }
    } catch (err: any) {
      console.error(' QR Generation Failed:', err);
      const backendMessage = err?.data?.message || err?.message || String(err);
      setSnackbar({
        open: true,
        message: backendMessage.includes('Order not found')
          ? `Order not found. Check whether this is a guest order and use guest QR endpoint.`
          : backendMessage,
        severity: 'error',
      });
    }
  };

  const handleDownloadQR = async () => {
    if (!generatedQR || !selectedOrder) {
      console.error(' Missing data for download:', {
        hasGeneratedQR: !!generatedQR,
        hasSelectedOrder: !!selectedOrder,
      });
      setSnackbar({
        open: true,
        message: 'Missing QR data or selected order. Please generate QR first.',
        severity: 'error',
      });
      return;
    }

    const orderType = getOrderType(selectedOrder);

    try {
      if (qrImageUrl) {
        const link = document.createElement('a');
        link.href = qrImageUrl;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: 'QR Code downloaded successfully!',
          severity: 'success',
        });
        return;
      }

      if (generatedQR.qrCode) {
        const cleanBase64 = generatedQR.qrCode.replace(/\s/g, '');
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${cleanBase64}`;
        link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbar({
          open: true,
          message: 'QR Code downloaded successfully!',
          severity: 'success',
        });
        return;
      }

      const apiUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${selectedOrder.id}/qr/image`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API returned ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Order_${selectedOrder.id}_${orderType}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'QR Code downloaded successfully!',
        severity: 'success',
      });
    } catch (error: any) {
      console.error(' Download Failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to download QR code: ${error?.message || 'Unknown error'}. Please try again.`,
        severity: 'error',
      });
    }
  };

  const handlePrintQR = () => {
    if (!qrPrintRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const imageSource = qrImageUrl || `data:image/png;base64,${generatedQR?.qrCode}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order QR Code - #${selectedOrder?.id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            .qr-container {
              text-align: center;
              border: 2px solid #000;
              padding: 30px;
              border-radius: 10px;
            }
            .order-info {
              margin-bottom: 20px;
            }
            .order-info h2 {
              margin: 0 0 10px 0;
            }
            .order-info p {
              margin: 5px 0;
              font-size: 14px;
            }
            .qr-image {
              margin: 20px 0;
            }
            .qr-image img {
              max-width: 300px;
              width: 100%;
              background-color: white;
            }
            .pickup-code {
              font-size: 24px;
              font-weight: bold;
              margin-top: 15px;
              letter-spacing: 2px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="order-info">
              <h2>Order #${selectedOrder?.id}</h2>
              <p>Customer: ${selectedOrder?.user_name}</p>
              <p>Date: ${selectedOrder?.order_date
        ? new Date(selectedOrder.order_date).toLocaleDateString()
        : '-'
      }</p>
              <p><strong>Total: AED ${selectedOrder?.total_order_amount || '0'}</strong></p>
            </div>
            <hr />
            <div class="qr-image">
              <img src="${imageSource}" alt="Order QR Code" />
            </div>
            <div class="pickup-code">
              <small>Pickup Code</small><br />
              ${generatedQR?.pickupCode || ''}
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: string | number | undefined) => {
    const n = Number(amount || 0);
    if (Number.isNaN(n)) return `AED ${amount}`;
    return `AED ${n.toFixed(2)}`;
  };

  const getStatusChip = (status: number) => {
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === status);
    return (
      <Chip
        label={statusOption?.label || 'Unknown'}
        size="small"
        sx={{
          backgroundColor: statusOption?.color || '#9e9e9e',
          color: 'white',
          fontWeight: 600,
          fontSize: '12px',
        }}
      />
    );
  };

  const requiresComment = (status: number) => {
    return [5, 6, 7, 8, 9, 10].includes(status);
  };

  const getCommentsForOrder = (orderId: number): OrderComment[] => {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return [];
    return order.comments || [];
  };

  const getCommentsForStatus = (orderId: number, status: number): OrderComment[] => {
    const allComments = getCommentsForOrder(orderId);
    return allComments.filter((comment) => comment.status === status);
  };

  const hasCommentsForStatus = (orderId: number, status: number) => {
    return getCommentsForStatus(orderId, status).length > 0;
  };

  const hasAnyComments = (orderId: number) => {
    const comments = getCommentsForOrder(orderId);
    return comments.length > 0;
  };

  // Helper function to construct proper image URL
  const getProductImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath) return '';
    
    // Remove any leading slash if present
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    
    // Check if it's already a full URL
    if (cleanPath.startsWith('http')) {
      return cleanPath;
    }
    
    // Check if it contains 'images/' directory
    if (cleanPath.includes('images/')) {
      return `https://fftestservice.sarsetech.com/${cleanPath}`;
    }
    
    // Default - assume it's in the images directory
    return `https://fftestservice.sarsetech.com/images/${cleanPath}`;
  };

  // --- UPDATED: render product items with proper images ---
  const renderOrderItems = (order: Order | null | undefined) => {
    if (!order) return null;
    const items = order.items || [];

    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No items found for this order.
        </Typography>
      );
    }

    return (
      <Box>
        {items.map((item: any, idx: number) => {
          const productName = item.product_name || item.name || `Product ${item.product_id || ''}`;
          const productImage = item.product_image || item.product_main_image || null;
          const imageUrl = getProductImageUrl(productImage);
          
          const qty = item.product_quantity || item.quantity || item.qty || 0;
          const unitPrice = item.unit_price || item.price || item.unitPrice || '0';
          const itemTotal = item.itemTotal || item.item_total || (Number(unitPrice) * Number(qty)).toFixed(2);
          const discount = item.discount || item.discountAmt || item.discount_amount || '0';
          const discountAmt = item.discountAmt || item.discountAmt || item.discount_amount || '0';
          // warehouse details could be on item or top-level order; handle both
          const warehouseId = item.warehouse_id ?? item.warehouse?.id ?? item.warehouseId ?? item.warehouseId;
          const warehouseName =
            item.warehouse_name ??
            (item.warehouse && (item.warehouse.name || item.warehouse.warehouse_name)) ??
            (item.warehouse?.name) ??
            '-';
          const warehouseExtra = item.warehouse_extra_cost ?? item.warehouse?.extra_cost ?? '0';

          return (
            <Box
              key={item.id || idx}
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                p: 2,
                borderRadius: 1,
                border: '1px solid #e9ecef',
                mb: 2,
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  flexShrink: 0,
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                  border: '1px solid #f1f1f1',
                  position: 'relative',
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={productName}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      // Fallback to placeholder
                      img.style.display = 'none';
                    }}
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }}>
                    <ImageIcon sx={{ color: '#ccc', fontSize: 32 }} />
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                      No Image
                    </Typography>
                  </Box>
                )}
                {imageUrl && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      fontSize: '10px',
                      px: 0.5,
                      py: 0.25,
                      textAlign: 'center',
                    }}
                  >
                    ID: {item.product_id}
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {productName}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Quantity
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {qty} × {formatCurrency(unitPrice)}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Item Total
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatCurrency(itemTotal)}
                    </Typography>
                  </Box>
                  
                  {Number(discount) > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Discount
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="error">
                        -{formatCurrency(discount)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: '#666' }}>
                    Warehouse Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Warehouse
                      </Typography>
                      <Typography variant="body2">
                        {warehouseName} (ID: {warehouseId || '-'})
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Extra Cost
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(warehouseExtra)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showDetails ? (
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#202223',
                      fontSize: { xs: '20px', md: '24px' },
                    }}
                  >
                    Orders
                  </Typography>
                </Box>

                <Card
                  sx={{
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    border: '1px solid #e1e3e5',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs
                      value={mainTabValue}
                      onChange={(e, v) => setMainTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        },
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />
                    </Tabs>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      borderBottom: '1px solid #e1e3e5',
                      display: 'flex',
                      gap: 1,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search orders"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{
                        maxWidth: { xs: '100%', sm: 400 },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 200,
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={
                                  ORDER_STATUS_OPTIONS.find((opt) => opt.value === value)?.label ||
                                  `Status ${value}`
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    ORDER_STATUS_OPTIONS.find((opt) => opt.value === value)
                                      ?.color || '#9e9e9e',
                                  color: 'white',
                                  fontSize: '11px',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {ORDER_STATUS_OPTIONS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            <Checkbox checked={statusFilter.indexOf(status.value) > -1} />
                            <ListItemText primary={status.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Warehouse dropdown filter */}
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 220,
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <InputLabel>Warehouse</InputLabel>
                      <Select
                        value={warehouseFilter}
                        onChange={handleWarehouseFilterChange}
                        label="Warehouse"
                        sx={{ backgroundColor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>All Warehouses</em>
                        </MenuItem>
                        {warehouses.map((w: any) => (
                          <MenuItem key={w.id} value={w.id}>
                            {w.name || `Warehouse ${w.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {(searchStr || statusFilter.length > 0 || warehouseFilter !== '') && (
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                          minWidth: { xs: '100%', sm: 'auto' },
                          '&:hover': {
                            borderColor: '#202223',
                            bgcolor: 'transparent',
                          },
                        }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Box>

                  {isLoading && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 300,
                        bgcolor: 'white',
                      }}
                    >
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load orders. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && filteredOrders.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 500 }}
                      >
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr || statusFilter.length > 0 || warehouseFilter !== ''
                          ? `No results matching your filters`
                          : 'Orders will appear here when customers place them'}
                      </Typography>
                      {(searchStr || statusFilter.length > 0 || warehouseFilter !== '') && (
                        <Button
                          variant="outlined"
                          onClick={handleClearFilters}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                          }}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                  )}

                  {!isLoading && paginatedOrders.length > 0 && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      <Box sx={{ minWidth: 1100 }}>
                        {/* Header row (desktop) */}
                        <Box
                          sx={{
                            display: { xs: 'none', md: 'grid' },
                            gridTemplateColumns: '80px 180px 180px 140px 80px 140px 140px 130px',
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #e1e3e5',
                            bgcolor: '#fafbfb',
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Order #
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            D - Agent
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Customer
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Date
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Items
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Total Amount
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                            }}
                          >
                            Status
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#6d7175',
                              fontWeight: 600,
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              textAlign: 'right',
                            }}
                          >
                            Actions
                          </Typography>
                        </Box>

                        {paginatedOrders.map((order: Order, index: number) => (
                          <React.Fragment key={order.id}>
                            <Box
                              sx={{
                                display: { xs: 'none', md: 'grid' },
                                gridTemplateColumns:
                                  '80px 180px 180px 140px 80px 140px 140px 130px',
                                gap: 2,
                                px: 2,
                                py: 2,
                                alignItems: 'center',
                                borderBottom:
                                  index < paginatedOrders.length - 1
                                    ? '1px solid #e1e3e5'
                                    : 'none',
                                '&:hover': { bgcolor: '#f9fafb' },
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                #{order.id}
                              </Typography>

                              {/* Agent column */}
                              <Box>{renderAgentInfo(order)}</Box>

                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: '#202223' }}
                                  noWrap
                                >
                                  {order.user_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  ID: {order.user_id}
                                </Typography>
                              </Box>

                              <Typography variant="body2" color="text.secondary">
                                {formatDate(order.order_date)}
                              </Typography>

                              {/* ITEMS COUNT: prefer order.order_items_count (API field), fallback to order.items.length */}
                              <Typography variant="body2" color="text.secondary">
                                {order.order_items_count ?? (order.items ? order.items.length : 0)} item
                                {(order.order_items_count ?? (order.items ? order.items.length : 0)) !== 1 ? 's' : ''}
                              </Typography>

                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: '#202223' }}
                              >
                                {formatCurrency(order.total_order_amount)}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getStatusChip(order.status)}
                                {hasAnyComments(order.id) && (
                                  <Comment color="info" fontSize="small" />
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  justifyContent: 'flex-end',
                                  flexWrap: 'nowrap',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrder(order)}
                                  sx={{
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' },
                                  }}
                                  title="View Details"
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleStatusUpdateClick(order)}
                                  sx={{
                                    color: '#6d7175',
                                    '&:hover': { bgcolor: '#f1f2f4' },
                                  }}
                                  title="Update Status"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleGenerateQR(order)}
                                  disabled={isGeneratingQR}
                                  sx={{
                                    color: '#008060',
                                    '&:hover': { bgcolor: '#f1f2f4' },
                                  }}
                                  title="Generate QR Code"
                                >
                                  {isGeneratingQR ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <QrCode2 fontSize="small" />
                                  )}
                                </IconButton>
                                {hasAnyComments(order.id) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewComments(order)}
                                    sx={{
                                      color: '#6d7175',
                                      '&:hover': { bgcolor: '#f1f2f4' },
                                    }}
                                    title="View Comments"
                                  >
                                    <Comment fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            {/* Mobile card view */}
                            <Card
                              sx={{
                                display: { xs: 'block', md: 'none' },
                                m: 2,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                borderRadius: 2,
                                border: '1px solid #e1e3e5',
                                overflow: 'hidden',
                              }}
                            >
                              <Box sx={{ p: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    mb: 2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#202223',
                                        fontSize: '16px',
                                      }}
                                    >
                                      Order #{order.id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDate(order.order_date)}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {getStatusChip(order.status)}
                                    {hasAnyComments(order.id) && (
                                      <Comment color="info" fontSize="small" sx={{ ml: 0.5 }} />
                                    )}
                                  </Box>
                                </Box>

                                {/* Agent info on mobile */}
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                  >
                                    Delivery Agent
                                  </Typography>
                                  {renderAgentInfo(order)}
                                </Box>

                                <Box
                                  sx={{
                                    mb: 2,
                                    pb: 2,
                                    borderBottom: '1px solid #e1e3e5',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      color: '#202223',
                                      mb: 0.5,
                                    }}
                                  >
                                    {order.user_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Customer ID: {order.user_id}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      Items
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {order.order_items_count ?? (order.items ? order.items.length : 0)} item
                                      {(order.order_items_count ?? (order.items ? order.items.length : 0)) !== 1 ? 's' : ''}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      Total Amount
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#202223',
                                        fontSize: '16px',
                                      }}
                                    >
                                      {formatCurrency(order.total_order_amount)}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => handleViewOrder(order)}
                                    sx={{
                                      flex: '1 1 auto',
                                      minWidth: '100px',
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      '&:hover': { borderColor: '#202223' },
                                    }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Edit />}
                                    onClick={() => handleStatusUpdateClick(order)}
                                    sx={{
                                      flex: '1 1 auto',
                                      minWidth: '100px',
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      '&:hover': { borderColor: '#202223' },
                                    }}
                                  >
                                    Update
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<QrCode2 />}
                                    onClick={() => handleGenerateQR(order)}
                                    disabled={isGeneratingQR}
                                    sx={{
                                      flex: '1 1 auto',
                                      minWidth: '100px',
                                      textTransform: 'none',
                                      bgcolor: '#008060',
                                      '&:hover': { bgcolor: '#006e52' },
                                    }}
                                  >
                                    QR Code
                                  </Button>
                                  {hasAnyComments(order.id) && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Comment />}
                                      onClick={() => handleViewComments(order)}
                                      sx={{
                                        flex: '1 1 100%',
                                        textTransform: 'none',
                                        borderColor: '#c9cccf',
                                        color: '#202223',
                                        '&:hover': { borderColor: '#202223' },
                                      }}
                                    >
                                      View Comments
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Card>
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, newPage) => setPage(newPage)}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // ------------- ORDER DETAILS (VIEW SCREEN) -------------
              <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' },
                    }}
                  >
                    Orders
                  </Button>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: '#202223',
                        fontSize: { xs: '20px', md: '24px' },
                      }}
                    >
                      Order #{selectedOrder?.id}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {getStatusChip(selectedOrder?.status || 0)}
                    </Box>
                  </Box>
                </Box>

                {/* FULL-WIDTH DESKTOP GRID INSTEAD OF SINGLE COLUMN STACK */}
                <Grid container spacing={2}>
                  {/* Customer */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#202223',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Person /> Customer
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Name:</strong> {selectedOrder?.user_name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>User ID:</strong> {selectedOrder?.user_id}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Delivery Agent */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Delivery Agent
                        </Typography>
                        {renderAgentInfo(selectedOrder)}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Payment Summary */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#202223',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <AttachMoney /> Payment Summary
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Subtotal
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_amount || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Discount
                          </Typography>
                          <Typography variant="body2">
                            -{formatCurrency(selectedOrder?.total_discount_amount || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Shipping
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_shipping_charge || '0')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Tax
                          </Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder?.total_tax_amount || '0')}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            pt: 2,
                            borderTop: '2px solid #e1e3e5',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Total
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: '16px' }}
                          >
                            {formatCurrency(selectedOrder?.total_order_amount || '0')}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Actions – separate row on desktop */}
                  <Grid item xs={12} md={4}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 2, fontWeight: 600, color: '#202223' }}
                        >
                          Actions
                        </Typography>
                        <Stack spacing={1}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              selectedOrder && handleStatusUpdateClick(selectedOrder)
                            }
                            startIcon={<Edit />}
                            sx={{
                              bgcolor: '#008060',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#006e52' },
                            }}
                          >
                            Update Status
                          </Button>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() =>
                              selectedOrder && handleGenerateQR(selectedOrder)
                            }
                            startIcon={<QrCode2 />}
                            disabled={isGeneratingQR}
                            sx={{
                              bgcolor: '#5c6ac4',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '8px',
                              '&:hover': { bgcolor: '#4c5ab8' },
                            }}
                          >
                            {isGeneratingQR ? 'Generating...' : 'Generate QR Code'}
                          </Button>
                          {hasAnyComments(selectedOrder?.id || 0) && (
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() =>
                                selectedOrder && handleViewComments(selectedOrder)
                              }
                              startIcon={<Comment />}
                              sx={{
                                textTransform: 'none',
                                borderColor: '#c9cccf',
                                color: '#202223',
                                borderRadius: '8px',
                                fontWeight: 500,
                                '&:hover': {
                                  borderColor: '#202223',
                                  bgcolor: 'transparent',
                                },
                              }}
                            >
                              View All Comments
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>

                  {/* PRODUCTS / ITEMS - full width card */}
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        borderRadius: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        border: '1px solid #e1e3e5',
                      }}
                    >
                      <Box sx={{ p: { xs: 2, md: 3 } }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Products ({selectedOrder?.order_items_count ?? (selectedOrder?.items ? selectedOrder.items.length : 0)})
                        </Typography>

                        {renderOrderItems(selectedOrder)}
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

          </Box>
        </Box>
      </Box>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => {
          setQrDialogOpen(false);
          if (qrImageUrl) {
            try {
              URL.revokeObjectURL(qrImageUrl);
            } catch { }
            setQrImageUrl(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Order QR Code</Typography>
          <IconButton
            onClick={() => {
              setQrDialogOpen(false);
              if (qrImageUrl) {
                try {
                  URL.revokeObjectURL(qrImageUrl);
                } catch { }
                setQrImageUrl(null);
              }
            }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div ref={qrPrintRef}>
            <Box className="qr-container" sx={{ textAlign: 'center', p: 3 }}>
              <Box className="order-info" sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Order #{selectedOrder?.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {selectedOrder?.user_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date:{' '}
                  {selectedOrder?.order_date
                    ? formatDate(selectedOrder.order_date)
                    : '-'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600, mt: 1 }}
                >
                  Total:{' '}
                  {selectedOrder?.total_order_amount
                    ? formatCurrency(selectedOrder.total_order_amount)
                    : '-'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {generatedQR && (
                <>
                  <Box
                    className="qr-image"
                    sx={{ display: 'flex', justifyContent: 'center', my: 3 }}
                  >
                    {isLoadingQrImage ? (
                      <CircularProgress />
                    ) : (
                      <img
                        src={
                          qrImageUrl ||
                          `data:image/png;base64,${generatedQR.qrCode}`
                        }
                        alt="Order QR Code"
                        style={{
                          maxWidth: '300px',
                          width: '100%',
                          border: '2px solid #e1e3e5',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                        }}
                        onError={(e) => {
                          console.error('Image failed to load');
                          const img = e.target as HTMLImageElement;
                          if (!img.src.startsWith('data:') && generatedQR?.qrCode) {
                            img.src = `data:image/png;base64,${generatedQR.qrCode}`;
                          }
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    className="pickup-code"
                    sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      Pickup Code
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: 3,
                        fontFamily: 'monospace',
                      }}
                    >
                      {generatedQR.pickupCode}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={() => {
              setQrDialogOpen(false);
              if (qrImageUrl) {
                try {
                  URL.revokeObjectURL(qrImageUrl);
                } catch { }
                setQrImageUrl(null);
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownloadQR}
            disabled={!generatedQR}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrintQR}
            disabled={!generatedQR}
            sx={{
              textTransform: 'none',
              bgcolor: '#008060',
              '&:hover': { bgcolor: '#006e52' },
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => {
                const newStatus = Number(e.target.value);
                setSelectedStatus(newStatus);
              }}
              label="Order Status"
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={isUpdating}>
            cancel 
          </Button>

          {/* {!requiresComment(selectedStatus) && (
            <Button
              variant="outlined"
              onClick={() => handleStatusUpdate()}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : null}
            >
              {isUpdating ? 'Updating...' : 'Update without comment'}
            </Button>
          )} */}

          <Button
            variant="contained"
            onClick={() => {
              if (requiresComment(selectedStatus)) {
                setStatusDialogOpen(false);
                setCommentDialogOpen(true);
              } else {
                handleStatusUpdate();
                setStatusDialogOpen(false);
              }
            }}
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {requiresComment(selectedStatus) ? 'Add Comment' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => {
          setCommentDialogOpen(false);
          setCommentText('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>
          Update Order Status{' '}
          {requiresComment(selectedStatus) ? '(Comment Required)' : '(Comment Optional)'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(Number(e.target.value))}
              label="Order Status"
            >
              {ORDER_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: option.color,
                      }}
                    />
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label={
              requiresComment(selectedStatus)
                ? 'Add a comment (required)'
                : 'Add a comment (optional)'
            }
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add your comment about this status change..."
            required={requiresComment(selectedStatus)}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {requiresComment(selectedStatus)
              ? 'Comments are required for status changes to: Not Delivered, Cancelled by User, Return and Replacement, Return and Refund, Order Cancelled, and Order Delayed.'
              : 'Comments are optional for this status change.'}
          </Typography>

          {selectedOrder &&
            hasCommentsForStatus(selectedOrder.id, selectedStatus) && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Existing comments for this status:
                </Typography>
                {getCommentsForStatus(selectedOrder.id, selectedStatus)
                  .slice()
                  .sort(
                    (a, b) =>
                      new Date(b.created_at || '').getTime() -
                      new Date(a.created_at || '').getTime()
                  )
                  .map((comment, index) => (
                    <CommentItem key={comment.id || index} comment={comment} />
                  ))}
              </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setCommentDialogOpen(false);
              setCommentText('');
            }}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCommentSubmit}
            disabled={isUpdating || (requiresComment(selectedStatus) && !commentText.trim())}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Comments Dialog */}
      <Dialog
        open={viewCommentsOpen}
        onClose={() => setViewCommentsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>All Comments for Order #{selectedOrder?.id}</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <CommentsList
              orderId={selectedOrder.id}
              maxHeight={500}
              showDeleteAllButton={true}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setViewCommentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Comments Dilog */}
      <Dialog
        open={deleteAllCommentsDialogOpen}
        onClose={() => setDeleteAllCommentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>Confirm Delete All Comments</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete all comments for Order #{selectedOrder?.id}?
          </Typography>

          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {deletableComments.length === (selectedOrder?.comments?.length || 0)
              ? `This will delete ${deletableComments.length} comment${deletableComments.length !== 1 ? 's' : ''
              } permanently. This action cannot be undone.`
              : `This will delete ${deletableComments.length} of ${selectedOrder?.comments?.length || 0
              } comment${(selectedOrder?.comments?.length || 0) !== 1 ? 's' : ''
              } permanently (only comments with statuses 5–10 are deletable). This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeleteAllCommentsDialogOpen(false)}
            disabled={isDeletingComment}
          >
            Cancel
          </Button>
          <Button
            variant
            ="contained"
            color="error"

            onClick={handleConfirmDeleteAllComments}
            
            disabled={isDeletingComment || deletableComments.length === 0}
            startIcon={isDeletingComment ? <CircularProgress size={20} /> : null}
          >
            {isDeletingComment ? 'Deleting...' : 'Delete All Comments'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default OrderManagement;